<script setup lang="ts">
import { ref, computed, defineEmits, watch, onBeforeMount, onUnmounted, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import axiosInstance from '../plugins/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useRoute } from 'vue-router'
import { useUserVariablesStore } from '../store/userVariables';
import router from '../router';
import { useAnswerStore } from '../store/answers';
import { useStyleStore } from '@/store/styleStore';
import QuestionHistory from './components/QuestionHistory.vue';
import OmnibusHint from './components/OmnibusHint.vue';


const translationsLoaded = ref(false);
const route = useRoute()
const userVariablesStore = useUserVariablesStore();
const reportNameModalVisible = ref(false);
const reportName = ref('');
const rawReportName = ref('');
const currentLanguage = ref(route.query.lang)
const translation = computed(() => userVariablesStore.currentLanguageData);
const languageCodes = ref<string[]>([]);
const toast = useToast();
const matchingEvaluation = ref([]);
const isAuthenticated = ref(false)
const evaluationIsLoading = ref(true)
const creatingReportLoading = ref(false)
const reportNameModalVisibleLocalStorage = ref(false)
const answerStore = useAnswerStore();
const inputHasInvalidChars = ref(false);
const styleStore = useStyleStore();


const includeAnswersInReport = ref(true);


let sanitizeTimeout: number | null = null;

const answers = computed(() => answerStore.answers);



const sanitizeFileName = (filename: string): string => {
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/g;
    return filename.replace(invalidChars, '');
};

watch(reportName, (newValue, oldValue) => {
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;

    if (invalidChars.test(newValue)) {
        inputHasInvalidChars.value = true;

        if (sanitizeTimeout) {
            clearTimeout(sanitizeTimeout);
        }

        sanitizeTimeout = setTimeout(() => {
            reportName.value = sanitizeFileName(newValue);

            setTimeout(() => {
                inputHasInvalidChars.value = false;
            }, 200);
        }, 500);
    }
});

const sleep = (milliseconds) => {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
};


const checkAuthentication = async () => {
    if (localStorage.getItem('token')) {
        try {
            axiosInstance.get('/user/isAuthenticated', {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            })
            isAuthenticated.value = true
        } catch (err) {
            console.log(err)
        }
    }
}




const removeLeadingBreak = (html: string): string => {
    if (!html) return '';
    html = html.trimStart();

    if (html.startsWith('<br>')) {
        return html.slice(4);
    } else if (html.startsWith('<p><br></p>')) {
        return html.slice(11);
    }

    return html;
};

const getResultsFromServer = async (reportID: string) => {
    try {
        const { data } = await axiosInstance.get(`/evaluations/prepareReport/${reportID}/${translation.value._id}`);
        console.log(data)
        matchingEvaluation.value = data;
        await sleep(1000);
        evaluationIsLoading.value = false;
    } catch (e) {
        console.error(e);
        toast.add({ severity: 'error', summary: 'Fehler', detail: 'Fehler beim Laden der Antworten.', life: 3000 });
    }
};



const createHBSreport = async () => {
    try {
        creatingReportLoading.value = true;
        const cleanReportName = sanitizeFileName(reportName.value);

        const postData = includeAnswersInReport.value
            ? { answers: answers.value }
            : {};
        const response = await axiosInstance.post(
            `/pdf/generate-report/${route.params.reportID}/${translation.value._id}/${cleanReportName}`,
            postData,
            { responseType: 'blob' }
        );

        if (response && response.data) {
            const blob = new Blob([response.data], { type: 'application/pdf' });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', cleanReportName + '.pdf');

            document.body.appendChild(link);
            creatingReportLoading.value = false;
            link.click();


            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
            reportNameModalVisible.value = false;
        } else {
            creatingReportLoading.value = false;
            reportNameModalVisible.value = false;
            console.error("Keine Daten für die PDF erhalten");
        }
    } catch (error) {
        creatingReportLoading.value = false;
        reportNameModalVisible.value = false;
        console.error("Fehler beim Herunterladen der PDF", error);
    }
};

watch(
    () => route.query.lang,
    async (newLang: any) => {
        if (newLang) {
            currentLanguage.value = newLang;
        }
    }
);


const initPage = async () => {
    await userVariablesStore.loadLanguageData(currentLanguage.value);
    languageCodes.value = userVariablesStore.allLanguageCodes;

    watch(
        () => translation.value,
        (newTranslation) => {
            if (newTranslation && Object.keys(newTranslation).length > 0) {
                translationsLoaded.value = true;
            }
        },
        { immediate: true }
    );
};


onBeforeMount(async () => {
    await initPage();
    userVariablesStore.setLanguage(currentLanguage.value);
});

watch(translationsLoaded, (loaded) => {
    if (loaded) {
        getResultsFromServer(route.params.reportID);
    }
});

await checkAuthentication()


const saveReportToLocalStorage = () => {
    const cleanReportName = sanitizeFileName(reportName.value);
    const reportID = route.params.reportID as string;
    const reportData = {
        id: reportID,
        name: cleanReportName,
        result: translation.value.evaluations.result[matchingEvaluation.value.result[0]].content,
    };

    const storedReports = JSON.parse(localStorage.getItem('savedReports') || '[]');
    storedReports.push(reportData);

    localStorage.setItem('savedReports', JSON.stringify(storedReports));

    toast.add({ severity: 'success', summary: 'Gespeichert', detail: 'Der Bericht wurde gespeichert.', life: 3000 });

    reportNameModalVisibleLocalStorage.value = false;
};


onMounted(async () => {
    let styles;

    if (styleStore.styles && Object.keys(styleStore.styles).length > 0) {
        styles = styleStore.styles;
    } else {
        styles = await styleStore.getStyles();
    }

    watch(
        () => styles.showAnswersOverview?.value ?? 'hidden',
        (newVal) => {
            if (newVal === 'hidden') {
                answerStore.answers = [];
            }
        },
        { immediate: true }
    );
});

const restartQuiz = () => {
    window.location.href = `/?lang=${currentLanguage.value}`;
}

onUnmounted(() => {
    if (sanitizeTimeout) {
        clearTimeout(sanitizeTimeout);
    }
});


</script>


<template>
    <div class="resultWrapper">
        <div v-if="!evaluationIsLoading" class="evaluationContainer">
                                    <!-- OMNIBUS HINT -->
            <OmnibusHint />
            <!-- OMNIBUS HINT -->

            <div>
                <!-- RESULT -->
                <p class="evaluationResultHeader h4" v-html="translation?.staticTexts?.evaluationResult"></p>
            </div>

            <div class="fw-bold mb-5 mt-5 text-center resultText"
                v-html="translation?.evaluations?.result[matchingEvaluation.result[0]]?.content">
            </div>

            <!-- RESULT -->

            <div>
                <!-- SUMMARIES -->
                <b>
                    <p class="mb-1 resultSectionHeader" v-html="translation?.staticTexts?.evaluationSummary"></p>
                </b>
                <div v-for="summaryId in matchingEvaluation?.summaries" v-bind:key="summaryId">
                    <div class="endPageTextBlock"
                        v-html="removeLeadingBreak(translation?.evaluations?.summaries[summaryId]?.content)"></div>
                </div>
                <!-- SUMMARIES -->
            </div>

            <div>
                <!-- JUSTIFICATIONS -->
                <b>
                    <p class="mt-3 mb-1 resultSectionHeader" v-html="translation?.staticTexts?.evaluationJustification">
                    </p>
                </b>
                <div v-for="justification in matchingEvaluation?.justifications" v-bind:key="justification">
                    <div class="endPageTextBlock"
                        v-html="removeLeadingBreak(translation?.evaluations?.justifications[justification]?.content)">
                    </div>
                </div>
                <!-- JUSTIFICATIONS -->
            </div>

            <!-- ANTWORTEN ÜBERSICHT -->
            <QuestionHistory :answers="answers" :translation="translation"></QuestionHistory>
        </div>

        

        <!-- Rest des Templates bleibt gleich -->
        <div v-if="evaluationIsLoading" class="m-5 justify-content-center flex pt-5"
            style="flex-direction: column; align-items: center;">
            <div class="shapes flex"></div>
            <div class="flex mt-5 text-center resultLoadingMessage" v-if="translation">
                {{ translation.staticTexts.loadingReportText }}
            </div>
        </div>

        <div class="mt-5 quiz-button-container row align-items-center justify-content-around">
            <Button class="quiz-button col-5" @click="restartQuiz()">
                {{ translation?.staticTexts?.restartButton }}
            </Button>
            <Button class="quiz-button col-5" @click="reportNameModalVisible = true">
                {{ translation?.staticTexts?.downloadPdfButton }}
            </Button>
        </div>

        <Dialog :showHeader="false" v-model:visible="reportNameModalVisible" modal :style="{ width: '25rem' }"
            :closable="false" class="downloadReportDialog">
            <p class="pt-5">
                {{ translation?.staticTexts?.reportDownloadDescription }}
            </p>
            <div class="flex items-center gap-4 mb-4 report-name-input-container">
                <InputText id="name" v-model="reportName" class="flex-auto report-name-input"
                    :class="{ 'invalid-chars-shake': inputHasInvalidChars }" autocomplete="off" />
            </div>
            <div class="flex flex-wrap justify-center gap-4" v-if="answers && answers.length > 0">
                <div class="flex items-center gap-2">
                    <Checkbox style="margin-right: 0px !important;" v-model="includeAnswersInReport"
                        inputId="includeAnswers" name="includeAnswers" class="includeAnswersInput" binary />
                    <label for="includeAnswers" class="includeAnswersLabel"> {{ translation?.staticTexts?.includeAnswers
                        || 'Include Answers in Report' }} </label>
                </div>
            </div>

            <div class="flex justify-content-end gap-2">
                <Button type="button" :label="translation.staticTexts.reportDownloadCloseButton" severity="secondary"
                    @click="reportNameModalVisible = false">
                </Button>
                <Button type="button" :label="translation.staticTexts.reportDownloadSaveButton" class="quiz-button"
                    style="font-weight: bold !important;" :loading="creatingReportLoading" @click="createHBSreport">
                </Button>

            </div>
        </Dialog>

        <!-- Dialog zum Speichern -->
        <Dialog v-model:visible="reportNameModalVisibleLocalStorage" modal :style="{ width: '25rem' }"
            :closable="false">
            <p>{{ translation.staticTexts.reportDownloadDescription }}</p>
            <div class="flex items-center gap-4 mb-4 report-name-input-container">
                <InputText id="name" v-model="reportName" class="flex-auto report-name-input"
                    :class="{ 'invalid-chars-shake': inputHasInvalidChars }" placeholder="Berichtsname eingeben"
                    autocomplete="off" />
            </div>
            <div class="flex justify-content-end gap-2">
                <Button type="button" label="Abbrechen" severity="secondary"
                    @click="reportNameModalVisibleLocalStorage = false"></Button>
                <Button type="button" label="Speichern" class="p-button-tuev"
                    style="background-color: var(--tuev-light-blue) !important;"
                    @click="saveReportToLocalStorage"></Button>
            </div>
        </Dialog>
    </div>
</template>

<style scoped>
.bg-primary {
    background-color: #00dce6 !important;
}

* {
    font-family: 'Soho Gothic Pro Regular';
}

.resultText {
    padding: 20px;
    margin: 20px 0;
    font-size: 1.5em;
}

.pathList {
    list-style-type: none;
    text-align: left;
}

/* Report Name Input Validation Styles */
.report-name-input-container {
    position: relative;
}

.report-name-input {
    transition: all 0.3s ease;
}

/* Subtile Animation für ungültige Zeichen */
.invalid-chars-shake {
    animation: pulse-red 0.5s ease-in-out;
    border-color: #e74c3c !important;
    box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.3);
    transition: all 0.2s ease;
}

@keyframes pulse-red {
    0% {
        border-color: #e74c3c;
        box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7);
    }

    50% {
        border-color: #c0392b;
        box-shadow: 0 0 0 4px rgba(231, 76, 60, 0);
    }

    100% {
        border-color: #e74c3c;
        box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.3);
    }
}





.alert-box {
    display: flex;
    align-items: flex-start;
    background-color: #fdf8e4;
    border-left: 4px solid #f39c12;
    padding: 1rem;
    border-radius: 4px;
    margin-top: 1.5rem;
    font-size: 0.9rem;
}

.alert-icon {
    color: #f39c12;
    margin-right: 0.75rem;
    font-size: 1.2rem;
    margin-top: 2px;
}

.alert-text strong {
    color: #a35e00;
}
</style>
